"use client";

import { motion } from "framer-motion";
import Image from "next/image";

export interface AtrFlightSimBlockProps {
    title?: string;
    subtitle?: string;
    description?: string;
    mainImage?: string;
    features?: { title: string; desc: string }[];
    galleryImages?: string[];
    style?: {
        backgroundColor?: string;
        paddingTop?: string;
        paddingBottom?: string;
        imageObjectFit?: any;
        imageObjectPosition?: string;
    };
}

export default function AtrFlightSimBlock({
    title = "ATR72-600 Flight Simulation",
    subtitle = "High-fidelity training",
    description = "Our ATR72-600 Flight Simulation system provides an unparalleled training environment for pilots. By replicating the exact cockpit layout, avionics, and flight dynamics of the real aircraft, we ensure that trainees gain the muscle memory and situational awareness required for safe operations.",
    mainImage = "https://images.unsplash.com/photo-1464037866556-6812c9d1c72e?q=80&w=2940&auto=format&fit=crop",
    features = [
        { title: "Product Type", desc: "Flight Simulator" },
        { title: "Industry", desc: "Aviation" },
        { title: "Year", desc: "2024" }
    ],
    galleryImages = [
        "https://images.unsplash.com/photo-1559067515-bf7d799b6d42?q=80&w=2670&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1540962351504-03099e0a754b?q=80&w=2670&auto=format&fit=crop"
    ],
    style = {}
}: AtrFlightSimBlockProps) {
    return (
        <div
            className="w-full relative overflow-hidden"
            style={{
                backgroundColor: style?.backgroundColor || "#1C1C1C",
                paddingTop: style?.paddingTop || "0rem",
                paddingBottom: style?.paddingBottom || "0rem",
            }}
        >
            {/* Hero Part */}
            <div className="relative h-[80vh] w-full overflow-hidden flex items-end pb-20">
                <div className="absolute inset-0 z-0">
                    <Image
                        src={mainImage}
                        alt="ATR72-600 Flight Simulation"
                        fill
                        className="transition-transform duration-700"
                        style={{
                            objectFit: style?.imageObjectFit || "cover",
                            objectPosition: style?.imageObjectPosition || "center"
                        }}
                        priority
                    />
                    <div className="absolute inset-0 bg-black/40" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1C] via-transparent to-transparent" />
                </div>

                <div className="relative z-10 container mx-auto px-6 md:px-20 lg:px-40">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-5xl md:text-8xl font-medium tracking-tight mb-6 text-white"
                    >
                        {title}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="text-xl md:text-2xl text-gray-300 max-w-2xl"
                    >
                        {subtitle}
                    </motion.p>
                </div>
            </div>

            {/* Content Section */}
            <div className="py-20 px-6 md:px-20 lg:px-40">
                <div className="container mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr] gap-16 md:gap-24">
                        <div>
                            <h2 className="text-3xl font-medium mb-6 text-white">Overview</h2>
                            <div className="w-full h-px bg-white/20 mb-8" />
                            <div className="space-y-4 text-gray-400 text-sm">
                                {(Array.isArray(features) ? features : []).map((feature, i) => (
                                    <p key={i}>
                                        <span className="text-white block mb-1">{feature.title}</span> {feature.desc}
                                    </p>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-8 text-lg md:text-xl text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: description }} />
                    </div>

                    {/* Gallery */}
                    <div className="mt-32 grid grid-cols-1 md:grid-cols-2 gap-8">
                        {(Array.isArray(galleryImages) ? galleryImages : [])
                            .filter(img => img && img.length > 0)
                            .map((img, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.2 }}
                                    className="relative aspect-[4/3] overflow-hidden rounded-sm"
                                >
                                    <Image
                                        src={img}
                                        alt={`Gallery Image ${i + 1}`}
                                        fill
                                        className="hover:scale-105 transition-transform duration-700"
                                        style={{
                                            objectFit: style?.imageObjectFit || "cover",
                                            objectPosition: style?.imageObjectPosition || "center"
                                        }}
                                    />
                                </motion.div>
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
